/**
 * Payment Success Page
 * Displayed after successful Stripe payment
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Loader2, Coins, Crown } from 'lucide-react';
import { usePayments } from '@/hooks/usePayments';
import { useToast } from '@/hooks/use-toast';

export const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { verifyPayment } = usePayments();

  const [isVerifying, setIsVerifying] = useState(true);
  const [paymentInfo, setPaymentInfo] = useState<{
    type: string;
    credits_added?: number;
    tier?: string;
    message: string;
  } | null>(null);

  const sessionId = searchParams.get('session_id');
  const paymentType = searchParams.get('type');

  useEffect(() => {
    const verify = async () => {
      if (!sessionId) {
        toast({
          title: 'Invalid Session',
          description: 'No payment session found',
          variant: 'destructive',
        });
        navigate('/preferences?tab=credits');
        return;
      }

      try {
        const result = await verifyPayment(sessionId);
        if (result) {
          setPaymentInfo(result);
        } else {
          // Error toast already shown by verifyPayment
          navigate('/preferences?tab=credits');
        }
      } catch (error) {
        console.error('Verification error:', error);
        navigate('/preferences?tab=credits');
      } finally {
        setIsVerifying(false);
      }
    };

    verify();
  }, [sessionId, verifyPayment, navigate, toast]);

  // Auto-redirect after 5 seconds
  useEffect(() => {
    if (!isVerifying && paymentInfo) {
      const timer = setTimeout(() => {
        navigate('/preferences?tab=credits');
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [isVerifying, paymentInfo, navigate]);

  if (isVerifying) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-background to-muted/20">
        <Card className="w-full max-w-md mx-4">
          <CardHeader>
            <div className="flex items-center justify-center mb-4">
              <Loader2 className="h-12 w-12 text-primary animate-spin" />
            </div>
            <CardTitle className="text-center">Verifying Payment</CardTitle>
            <CardDescription className="text-center">
              Please wait while we confirm your payment...
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (!paymentInfo) {
    return null; // Will redirect
  }

  const isCreditPurchase = paymentInfo.type === 'credit_purchase';
  const isSubscription = paymentInfo.type === 'subscription';

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-background to-muted/20">
      <Card className="w-full max-w-md mx-4">
        <CardHeader>
          <div className="flex items-center justify-center mb-4">
            <div className="rounded-full bg-green-100 dark:bg-green-900/20 p-3">
              <CheckCircle2 className="h-12 w-12 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <CardTitle className="text-center">Payment Successful!</CardTitle>
          <CardDescription className="text-center">
            {paymentInfo.message}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {isCreditPurchase && paymentInfo.credits_added && (
            <div className="flex items-center justify-center p-6 bg-gradient-to-r from-primary/10 to-primary-glow/10 rounded-lg border border-primary/20">
              <div className="text-center">
                <Coins className="h-8 w-8 text-primary mx-auto mb-2" />
                <p className="text-2xl font-bold text-primary">
                  +{paymentInfo.credits_added} Credits
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Added to your account
                </p>
              </div>
            </div>
          )}

          {isSubscription && paymentInfo.tier && (
            <div className="flex items-center justify-center p-6 bg-gradient-to-r from-primary/10 to-primary-glow/10 rounded-lg border border-primary/20">
              <div className="text-center">
                <Crown className="h-8 w-8 text-primary mx-auto mb-2" />
                <p className="text-2xl font-bold text-primary capitalize">
                  {paymentInfo.tier} Tier
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Subscription activated
                </p>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <Button
              className="w-full"
              onClick={() => navigate('/preferences?tab=credits')}
            >
              Go to Credits & Billing
            </Button>
            <Button
              className="w-full"
              variant="outline"
              onClick={() => navigate('/')}
            >
              Return to Home
            </Button>
          </div>

          <p className="text-xs text-center text-muted-foreground">
            Redirecting automatically in 5 seconds...
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
